import SectionView from '@/components/SectionView';
import agencyCommissionsQuery from '@/api/queries/agencyCommissions';
import agencyCommissionsMutation from '@/api/mutitations/agencyCommissions';
import { useNavigate } from 'react-router-dom';
import prepareTranslate from '@/helpers/dictionary';
import routes from '@/routes';
import { useEffect, useState } from 'react';
import Modal from '@/components/DetailsModal';
import { Button } from '@mantine/core';
import { IconEdit } from '@tabler/icons-react';
import { formatDateToDDMMYYYY } from '@/helpers/string';
import PropTypes from 'prop-types';
import { useDisclosure } from '@mantine/hooks';
import DownloadFile from '../DownloadFile';
import TieredCommissionView from '../TieredCommissionView';
import styles from './styles.module.css';

function AgencyCommissionsView(props) {
  const {
    open,
    close,
    opened,
    id,
    onReady,
  } = props;
  const t = prepareTranslate();
  const navigate = useNavigate();
  const [historyOpened, { open: historyOpen, close: historyClose }] = useDisclosure(false);

  const {
    data: agencyCommissionData,
    status,
    refetch,
  } = agencyCommissionsQuery.useGetAgencyCommissionsById({ id });

  const { mutateAsync: getHistoryMutation } = agencyCommissionsMutation.useGetArchivedCommissions();
  const [archivedCommissionsData, setArchivedCommissionsData] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);

  useEffect(() => {
    if (opened && id) {
      setModalVisible(false);
      refetch();
    } else {
      setModalVisible(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, id]);

  useEffect(() => {
    if (opened && agencyCommissionData) {
      getHistoryMutation({
        company: agencyCommissionData?.company?._id,
        product: agencyCommissionData?.product?.map((p) => p._id),
      }).then((history) => {
        setArchivedCommissionsData(history || []);
        setModalVisible(true);
        if (onReady) onReady();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, agencyCommissionData]);

  useEffect(() => {
    if (!agencyCommissionData && status !== 'pending') {
      navigate(routes.agencyCommissions.path);
    }
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  const buildCommissionLines = (commission) => {
    const productValue = commission?.product?.map((item) => `${item.hebrew}`).join(', ');
    const volumeFeesStartDateValue = commission?.volumeFeesStartDate
      ? formatDateToDDMMYYYY(commission.volumeFeesStartDate)
      : '';
    const payeeFeesStartDateValue = commission?.payeeFeesStartDate
      ? formatDateToDDMMYYYY(commission.payeeFeesStartDate)
      : '';
    const isVolumeFeesVatIncludedValue = commission?.isVolumeFeesVatIncluded ? 'כן' : 'לא';
    const isPayeeFeesVatIncludedValue = commission?.isPayeeFeesVatIncluded ? 'כן' : 'לא';
    const payeeFeesValidityDateValue = commission?.payeeFeesValidityDate
      ? formatDateToDDMMYYYY(commission.payeeFeesValidityDate)
      : '';
    const volumeFeesValidityDateValue = commission?.volumeFeesValidityDate
      ? formatDateToDDMMYYYY(commission.volumeFeesValidityDate)
      : '';
    return [
      [
        commission?.company?.hebrew && {
          title: t('pages.agencyCommissionsView.details.company'),
          value: commission.company.hebrew,
        },
        productValue && {
          title: t('pages.agencyCommissionsView.details.product'),
          value: productValue,
        },
        commission?.agentNumber && {
          title: t('pages.agencyCommissionsView.details.agentNumber'),
          value: commission.agentNumber,
        },
      ].filter(Boolean),
      [
        payeeFeesStartDateValue && {
          title: t('pages.agencyCommissionsView.details.payeeFeesStartDate'),
          value: payeeFeesStartDateValue,
        },
        payeeFeesValidityDateValue && {
          title: t('pages.agencyCommissionsView.details.payeeFeesValidityDate'),
          value: payeeFeesValidityDateValue,
        },
        commission?.agencyFeeMonthlyDeposit > 0 && {
          title: t('pages.agencyCommissionsView.details.agencyFeeMonthlyDeposit'),
          value: commission.agencyFeeMonthlyDeposit,
        },
        commission?.agencyFeeMonthlyAccumulation > 0 && {
          title: t('pages.agencyCommissionsView.details.agencyFeeMonthlyAccumulation'),
          value: commission.agencyFeeMonthlyAccumulation,
        },
        {
          title: t('pages.agencyCommissionsView.details.isPayeeFeesVatIncluded'),
          value: isPayeeFeesVatIncludedValue,
        },
      ].filter(Boolean),
      [
        volumeFeesStartDateValue && {
          title: t('pages.agencyCommissionsView.details.volumeFeesStartDate'),
          value: volumeFeesStartDateValue,
        },
        volumeFeesValidityDateValue && {
          title: t('pages.agencyCommissionsView.details.volumeFeesValidityDate'),
          value: volumeFeesValidityDateValue,
        },
        {
          title: t('pages.agencyCommissionsView.details.isVolumeFeesVatIncluded'),
          value: isVolumeFeesVatIncludedValue,
        },
        commission?.comments && {
          title: t('pages.agencyCommissionsView.details.comments'),
          value: commission.comments,
        },
      ].filter(Boolean),
    ].filter((group) => group.length > 0);
  };

  const currentCommissionLines = buildCommissionLines(agencyCommissionData);

  const handleViewHistoryClick = () => {
    close();
    historyOpen();
  };

  const actions = archivedCommissionsData?.length > 0 ? (
    <Button
      onClick={handleViewHistoryClick}
      leftSection={<IconEdit />}>
      {t('pages.agencyCommissionsView.action')}
    </Button>
  ) : null;

  const archivedModalLabel = t('pages.agencyCommissionsView.action');

  return (
    <>
      <Modal
        open={open}
        close={close}
        opened={modalVisible}
        label={t('pages.agencyCommissionsView.title')}
        actions={actions}
        size='90%'
      >
        <SectionView
          lines={currentCommissionLines}
          show={false}
        />
        <TieredCommissionView
          tiers={agencyCommissionData?.agencyFeeAnnualDeposit}
          label={t('pages.agencyCommissionsCreate.form.agencyFeeAnnualDeposit')}
        />
        <TieredCommissionView
          tiers={agencyCommissionData?.agencyFeeAnnualAccumulation}
          label={t('pages.agencyCommissionsCreate.form.agencyFeeAnnualAccumulation')}
        />
        {agencyCommissionData?.files?.map((file, index) => (
          <DownloadFile key={index} file={file} />
        ))}
      </Modal>

      <Modal
        padding="none"
        opened={historyOpened}
        close={() => historyClose()}
        label={archivedModalLabel}
      >
        {archivedCommissionsData?.map((item, index) => (
          <div
          className={
            `${styles.section} 
             ${index === archivedCommissionsData.length - 1 ? styles.lastSection : ''}`
            }
          key={index}
          >
            <SectionView lines={buildCommissionLines(item)} show={false} />
            <TieredCommissionView
              tiers={item?.agencyFeeAnnualDeposit}
              label={t('pages.agencyCommissionsCreate.form.agencyFeeAnnualDeposit')}
            />
            <TieredCommissionView
              tiers={item?.agencyFeeAnnualAccumulation}
              label={t('pages.agencyCommissionsCreate.form.agencyFeeAnnualAccumulation')}
            />
            {item?.files?.map((file, fileIndex) => (
              <DownloadFile key={fileIndex} file={file} />
            ))}
          </div>

        ))}
      </Modal>
    </>
  );
}

AgencyCommissionsView.propTypes = {
  open: PropTypes.func,
  close: PropTypes.func,
  opened: PropTypes.bool,
  id: PropTypes.string,
  onReady: PropTypes.func,
};

export default AgencyCommissionsView;
